import { addBreadcrumb, captureException } from "@sentry/browser";
import type { SeverityLevel } from "@sentry/browser";
import { GoogleBotError } from "@/helpers/sentry";

import type { WorkerCommand, WorkerMessage } from "./OfflineService";

import { Registration } from "../../../modules/service-worker/registration";
import { assertNever } from "../../../modules/shared-utils/assertions";
import { randomUUID } from "../../../modules/shared-utils/crypto";
import { promiseEvent } from "../../../modules/shared-utils/promise";
import { retry } from "../../../modules/shared-utils/retry";

const SW_CONTROLLER_MAX_WAIT_MS = 10000;
const SW_REGISTER_MAX_TRIES = 3;

function isWorkerOfRegistration(
  worker: ServiceWorker | null | undefined,
  registration: ServiceWorkerRegistration
): boolean {
  if (!worker) {
    return false;
  }
  return (
    worker === registration.installing ||
    worker === registration.waiting ||
    worker === registration.active
  );
}

let workerIdCounter = 0;
const workerIdMap = new WeakMap<ServiceWorker, `sw-${string}`>();
function workerId(worker: ServiceWorker | null | undefined): `sw-${string}` {
  if (!worker) {
    return "sw-<none>";
  }
  if (workerIdMap.has(worker)) {
    return workerIdMap.get(worker)!;
  }
  const id = `sw-${++workerIdCounter}` as const;
  workerIdMap.set(worker, id);
  return id;
}

const swStatesKeys = [
  "active",
  "waiting",
  "installing",
] as const satisfies Array<keyof ServiceWorkerRegistration>;

type SwStateKey = (typeof swStatesKeys)[number];
type SwStateLabel = `sw-${string} (${ServiceWorkerState})`;

function registrationStatesMap(
  registration?: ServiceWorkerRegistration | null
): Record<SwStateKey | "controller", SwStateLabel> {
  const map = {} as Record<SwStateKey | "controller", SwStateLabel>;
  swStatesKeys.forEach((key) => {
    const worker = registration?.[key];
    if (worker) {
      map[key] = `${workerId(worker)} (${worker.state})`;
    }
  });
  const controller = navigator.serviceWorker.controller;
  if (controller) {
    map.controller = `${workerId(controller)} (${controller.state})`;
  }
  return map;
}

function isServiceWorker(
  source: MessageEventSource | EventTarget | null
): source is ServiceWorker {
  if (!source) return false;

  return "state" in source && typeof source["state"] === "string";
}

class SWMessageTimeoutError extends Error {
  constructor(
    private readonly msg: SWMessage<unknown>,
    ms: number
  ) {
    super(`SW message timed out after ${ms}ms`);
    this.name = "SWMessageTimeoutError";
  }

  toJSON() {
    return {
      id: this.msg.uuid,
      key: this.msg.key,
      data: this.msg.value,
    };
  }
}

class SWRedundantError extends Error {
  constructor() {
    super("SW became redundant");
    this.name = "SWRedundantError";
  }
}

class NoSWFoundError extends Error {
  constructor() {
    super("No service worker found on registration");
    this.name = "NoSWFoundError";
  }
}

export class SWMessageEvent<TData> extends Event {
  public readonly source: MessageEventSource | null;
  public readonly data: TData;
  constructor(original: MessageEvent) {
    super("message");
    this.source = original.source;
    this.data = original.data;
  }
}

class SWMessage<T> {
  private timeout: ReturnType<typeof setTimeout> | undefined = undefined;
  private readonly finalizers: Array<() => void> = [];
  private readonly _resolve: (value: T | PromiseLike<T>) => void;
  private readonly _reject: (reason?: unknown) => void;

  public readonly uuid: string;
  public readonly promise: Promise<T>;

  constructor(
    public readonly key: WorkerCommand,
    public readonly value: unknown,
    messages: Map<string, SWMessage<unknown>>
  ) {
    this.uuid = randomUUID();
    messages.set(this.uuid, this as SWMessage<unknown>);
    this.finalizers.push(() => messages.delete(this.uuid));
    this.finalizers.push(() => clearTimeout(this.timeout));

    const { promise, resolve, reject } = Promise.withResolvers<T>();
    this._resolve = resolve;
    this._reject = reject;
    this.promise = promise;
  }

  public send(target: MessageEventSource) {
    target.postMessage({
      id: this.uuid,
      key: this.key,
      value: this.value,
    });
  }

  public setTimeout(ms: number) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(
      () =>
        this.reject(new SWMessageTimeoutError(this as SWMessage<unknown>, ms)),
      ms
    );
  }

  private reject(reason?: unknown) {
    this._reject(reason);
    this.finalize();
  }

  public resolve(value: T | PromiseLike<T>) {
    this._resolve(value);
    this.finalize();
  }

  private finalize() {
    this.finalizers.forEach((finalize) => finalize());
  }
}

export default class ReaderServiceWorker extends EventTarget {
  private readonly registration!: Promise<ServiceWorkerRegistration>;
  private worker!: Promise<ServiceWorker>;

  private readonly _messages = new Map<string, SWMessage<unknown>>();

  constructor() {
    super();

    if (!this.supported) {
      this.addBreadcrumb("Service Workers not supported", "warning");
      return;
    }

    this.handleMessage = this.handleMessage.bind(this);
    navigator.serviceWorker.addEventListener("message", this.handleMessage);
    navigator.serviceWorker.addEventListener("controllerchange", async () => {
      const registration = await this.registration.catch<
        ServiceWorkerRegistration | undefined
      >(() => undefined);
      this.addBreadcrumb("SW controller changed", {
        // @ts-expect-error TS doesn't know that controller can be undefined
        controller: null,
        ...registrationStatesMap(registration),
      });
    });

    // don't wait for page load to complete to handle messages
    navigator.serviceWorker.startMessages?.(); // startMessages was added later in the spec

    this.registration = this.obtainRegistration();
    // ensure we explicitly catch those errors
    // avoid unhandled promise rejections and have full stack trace
    this.registration.catch((err) => this.handleError(err));

    this.worker = this.registration.then((reg) => this.getActivatedWorker(reg));
  }

  public get supported(): boolean {
    return "serviceWorker" in navigator;
  }

  private _controller: ServiceWorker | null = null;
  public async untilControlledBy(): Promise<void> {
    if (!this.supported) {
      throw new Error("SW not supported");
    }
    const registration = await this.registration;

    let controller = navigator.serviceWorker.controller;
    // memoize the check to not pollute Sentry with multiple breadcrumbs
    if (controller && controller === this._controller) {
      return;
    }

    // page is already controlled by workers of this registration
    if (isWorkerOfRegistration(controller, registration)) {
      this._controller = controller;
      this.addBreadcrumb(
        "Page already controlled by SW",
        registrationStatesMap(registration)
      );
      return;
    }

    const { promise, resolve, reject } = Promise.withResolvers<void>();

    let changeTimeout: ReturnType<typeof setTimeout>;
    let callback: () => void;

    const complete = (err?: Error) => {
      clearTimeout(changeTimeout);
      navigator.serviceWorker.removeEventListener("controllerchange", callback);
      if (err) {
        reject(err);
      } else {
        resolve();
      }
    };
    callback = () => {
      controller = navigator.serviceWorker.controller;
      if (isWorkerOfRegistration(controller, registration)) {
        this._controller = controller;
        this.addBreadcrumb(
          "SW controller changed to expected SW",
          registrationStatesMap(registration)
        );
        complete();
      } else {
        this.addBreadcrumb(
          "SW controller changed but not controlled by expected SW",
          "warning",
          {
            scope: Registration.scope,
            url: Registration.url,
            ...registrationStatesMap(registration),
          }
        );
      }
    };

    changeTimeout = setTimeout(
      () => complete(new Error("SW controller change timed out")),
      SW_CONTROLLER_MAX_WAIT_MS
    );
    navigator.serviceWorker.addEventListener("controllerchange", callback);

    // for an already active SW, it might have already claimed its clients
    if (registration.active) {
      this.sendMessage("claim", null, registration.active).catch((err) =>
        captureException(err, {
          extra: {
            scope: Registration.scope,
            url: Registration.url,
            ...registrationStatesMap(registration),
          },
        })
      );
    }

    return promise;
  }

  private async obtainRegistration(): Promise<ServiceWorkerRegistration> {
    let state: "existing" | "registered";
    let registration = await retry(
      () => Registration.get((result) => (state = result)),
      {
        maxAttempts: SW_REGISTER_MAX_TRIES,
        shouldRetry: (attempts, error) => {
          // the stack trace indicates that the page is being crawled by GoogleBot
          // which doesn't support SW and will always fail to register it
          if ((error as Error)?.stack?.includes("wrsParams.serviceWorkers")) {
            // fail fast and replace current error
            this.addBreadcrumb("This appears to be a GoogleBot crawl", "error");
            throw new GoogleBotError(error as Error);
          }

          if (!error) {
            return false;
          }

          this.addBreadcrumb(
            "Service worker registration failed, retrying",
            "warning",
            {
              error: String(error),
              attempts: `${attempts}/${SW_REGISTER_MAX_TRIES}`,
            }
          );
          return true;
        },
      }
    );

    switch (state) {
      case "existing":
        this.addBreadcrumb(
          "Found existing SW registration",
          registrationStatesMap(registration)
        );
        break;
      case "registered":
        this.addBreadcrumb(
          "Registered service worker",
          registrationStatesMap(registration)
        );
        break;
      default:
        assertNever(state);
    }

    swStatesKeys.forEach((key) => {
      const worker = registration![key];
      if (worker) {
        this.watchState(worker);
      }
    });
    registration.addEventListener("updatefound", () => {
      this.addBreadcrumb(
        "Found update on SW registration",
        registrationStatesMap(registration)
      );
      this.watchState(registration.installing!);
    });
    return registration;
  }

  private async getActivatedWorker(
    reg: ServiceWorkerRegistration
  ): Promise<ServiceWorker> {
    for (const key of swStatesKeys) {
      const worker = reg[key];
      switch (worker?.state) {
        case "activated":
          // already active worker, resolve with this one
          return worker;
        case "redundant":
          // this worker is not usable, skip it
          this.addBreadcrumb(
            `${key} SW is redundant, waiting for new active SW`,
            "warning",
            registrationStatesMap(reg)
          );
          continue;
        case undefined:
          // no SW for this key, skip it
          continue;
        default:
          // any other state, we'll wait for it to become active (or redundant)
          return await promiseEvent(
            worker!, // not null because of previous switch case
            "statechange",
            (worker, _event, resolve) => {
              if (worker.state === "activated") {
                resolve(worker);
              } else if (worker.state === "redundant") {
                throw new SWRedundantError();
              }
            }
          );
      }
    }

    // should not happen, we expect at least an installing worker after registration
    // maybe registration was deleted?
    const registration = await navigator.serviceWorker.getRegistration(
      Registration.scope
    );

    this.addBreadcrumb("No SW found on registration", "error", {
      registrationsMatch: reg === registration,
      originalRegistration: registrationStatesMap(reg),
      currentRegistration: registration
        ? registrationStatesMap(registration)
        : "<no registration>",
    });
    throw new NoSWFoundError();
  }

  public async sendMessage<T>(
    key: WorkerCommand,
    value: unknown,
    sendTo: ServiceWorker | null = null
  ): Promise<T> {
    if (!this.supported) {
      throw new Error("SW not supported");
    }
    if (sendTo && !isWorkerOfRegistration(sendTo, await this.registration)) {
      throw new Error(
        "Attempting to send to a SW not part of the SW registration"
      );
    }
    const worker = sendTo ?? (await this.worker);
    const msg = new SWMessage<T>(key, value, this._messages);
    this.addBreadcrumb("Sending message", {
      worker: workerId(worker),
      message: { key, value, id: msg.uuid },
    });
    msg.send(worker);
    msg.setTimeout(3000);
    return await msg.promise;
  }

  private async handleMessage(event: MessageEvent) {
    const source = event.source;
    if (!isServiceWorker(source)) {
      return;
    }
    const reg = await this.registration;
    if (!isWorkerOfRegistration(source, reg)) {
      return;
    }

    const data = event.data as WorkerMessage;
    if (data.key?.startsWith("_")) {
      this.addBreadcrumb("Received message", {
        worker: workerId(source),
        message: event.data,
      });

      // forward event
      this.dispatchEvent(new SWMessageEvent<WorkerMessage>(event));
    } else if (data.id && this._messages.has(data.id)) {
      this.addBreadcrumb("Received reply", {
        worker: workerId(source),
        message: event.data,
      });

      // resolve message's promise
      const msg = this._messages.get(data.id)!;
      msg.resolve(data);
    } else {
      this.addBreadcrumb("Received unknown message", "warning", {
        worker: workerId(source),
        message: event.data,
      });
    }
  }

  private watchState(worker: ServiceWorker) {
    worker.addEventListener("statechange", async () => {
      this.addBreadcrumb(`SW state changed to ${worker.state}`, {
        worker: workerId(worker),
      });
      if (worker.state !== "redundant") {
        return;
      }
      let newWorker: ServiceWorker | null = null;
      try {
        const reg = await this.registration;
        newWorker = await (this.worker = this.getActivatedWorker(reg));
        this.addBreadcrumb("RE-Sending messages after SW shutdown", {
          oldWorker: workerId(worker),
          newWorker: workerId(newWorker),
          messages: Array.from(this._messages.keys()),
        });
        for (const msg of this._messages.values()) {
          msg.send(newWorker);
          msg.setTimeout(2000); // reset timeout, shorter
        }
      } catch (err) {
        this.handleError(err, {
          oldWorker: workerId(worker),
          newWorker: workerId(newWorker),
          messages: Array.from(this._messages.keys()),
        });
      }
    });
  }

  private handleError(error: unknown, extras: Record<string, unknown> = {}) {
    captureException(error, {
      extra: {
        sw_url: Registration.url,
        sw_scope: Registration.scope,
        ...extras,
      },
    });
  }

  private addBreadcrumb(
    message: string,
    level?: SeverityLevel,
    extra?: Record<string, unknown>
  ): void;
  private addBreadcrumb(
    message: string,
    extra?: Record<string, unknown>,
    level?: SeverityLevel
  ): void;
  private addBreadcrumb(
    message: string,
    levelOrExtra?: SeverityLevel | Record<string, unknown>,
    extraOrLevel?: Record<string, unknown> | SeverityLevel
  ): void {
    // swap parameters according to call signature
    const level: SeverityLevel =
      typeof levelOrExtra === "string"
        ? (levelOrExtra as SeverityLevel)
        : (extraOrLevel as SeverityLevel) || "info";
    const extra: Record<string, unknown> =
      typeof levelOrExtra === "string"
        ? (extraOrLevel as Record<string, unknown>) || {}
        : (levelOrExtra as Record<string, unknown>) || {};
    addBreadcrumb({
      category: "service-worker",
      message,
      level,
      data: {
        scope: Registration.scope,
        url: Registration.url,
        ...extra,
      },
    });
  }
}
